import { useState, useEffect } from 'react';
import { TenantConfigProvider } from './context/TenantConfig';
import { useTenantStatus } from './context/useTenantConfig';
import { AuthProvider } from './context/AuthProvider';
import { useAuthState, useAuthActions } from './context/useAuth';
import { BrandHeader } from './components/BrandHeader';
import { TrustFooter } from './components/TrustFooter';
import MarketingHero from './components/MarketingHero';
import { SignInPanel } from './components/SignInPanel';
import { AdminDashboard } from './components/AdminDashboard';
import { SelyoWorkspace } from './components/SelyoWorkspace';
import ThankYou from './components/ThankYou';
import { verifyDeterministicLicense } from './utils/mintCore';
import { AlertCircle, Check, Copy, ArrowRight } from 'lucide-react';
import { OAuthGateway } from './components/OAuthGateway';

function AppShell() {
  const status = useTenantStatus();
  const [currentPath, setCurrentPath] = useState(() => {
    return window.location.pathname;
  });
  const [currentHash, setCurrentHash] = useState(window.location.hash);

  const [clockTampered, setClockTampered] = useState(() => {
    const highestTimeStr = localStorage.getItem('selyo_highest_time');
    const trueNow = Date.now();
    const perfNow = performance.timeOrigin + performance.now();
    if (Math.abs(trueNow - perfNow) > 60000) {
      return true;
    }
    if (highestTimeStr) {
      const highestTime = Number(highestTimeStr);
      if (trueNow < highestTime) {
        return true;
      }
    }
    localStorage.setItem('selyo_highest_time', trueNow.toString());
    return false;
  });

  useEffect(() => {
    if (clockTampered) return;
    const interval = setInterval(() => {
      const highestTimeStr = localStorage.getItem('selyo_highest_time');
      const trueNow = Date.now();
      const perfNow = performance.timeOrigin + performance.now();
      
      if (Math.abs(trueNow - perfNow) > 60000) {
        setClockTampered(true);
        return;
      }

      if (highestTimeStr) {
        const highestTime = Number(highestTimeStr);
        if (trueNow < highestTime) {
          setClockTampered(true);
          return;
        }
      }
      localStorage.setItem('selyo_highest_time', trueNow.toString());
    }, 5000);
    return () => clearInterval(interval);
  }, [clockTampered]);

  // Email verification parsing status states
  const [verifyingToken, setVerifyingToken] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<{ success: boolean; message: string } | null>(null);

  const { signUp } = useAuthActions();

  useEffect(() => {
    const isLicenseActive = localStorage.getItem('selyo_license_active') === 'true';
    const licenseExpiry = Number(localStorage.getItem('selyo_license_expiry') || 0);
    if (isLicenseActive && licenseExpiry > 0 && Date.now() > licenseExpiry) {
      localStorage.setItem('selyo_license_active', 'false');
    }
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
      setCurrentHash(window.location.hash);
    };
    const handleHashChange = () => {
      setCurrentHash(window.location.hash);
    };
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('hashchange', handleHashChange);

    // Detect verification token in URL params on boot
    const urlParams = new URLSearchParams(window.location.search);
    const verifyToken = urlParams.get('verify_token');
    
    if (verifyToken) {
      setVerifyingToken(true);
      
      fetch('/api/auth/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: verifyToken }),
      })
        .then((res) => res.text().then((text) => text ? JSON.parse(text) : {}))
        .then((data) => {
          if (data.success && data.email) {
            localStorage.setItem('cf_email_verified', 'true');
            localStorage.setItem('cf_verified_email', data.email);
            setVerificationStatus({ success: true, message: `Email verified successfully: ${data.email}` });
            
            // Clean URL query parameters
            window.history.replaceState({}, document.title, window.location.pathname);
            
            // Check if we have cached register credentials to automatically initialize/unlock local vault
            const cachedEmail = sessionStorage.getItem('cf_temp_register_email');
            const cachedPass = sessionStorage.getItem('cf_temp_register_pass');
            if (cachedEmail && cachedPass && cachedEmail.toLowerCase() === data.email.toLowerCase()) {
              signUp(cachedEmail, cachedPass).catch(err => {
                console.error("Auto sign up failed after verification:", err);
              });
            }
          } else {
            setVerificationStatus({ success: false, message: data.message || 'Verification token is invalid or expired.' });
          }
        })
        .catch(() => {
          setVerificationStatus({ success: false, message: 'Failed to contact verification server endpoint.' });
        })
        .finally(() => {
          setVerifyingToken(false);
        });
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const navigate = (path: string) => {
    window.history.pushState({}, '', path);
    setCurrentPath(path);
    setCurrentHash(window.location.hash);
  };

  if (clockTampered) {
    return (
      <div className="min-h-screen bg-white text-slate-950 flex flex-col justify-center items-center p-6 text-center antialiased">
        <div className="max-w-md p-8 bg-[#F8F9FA] border border-slate-200 rounded-3xl shadow-lg">
          <div className="h-16 w-16 bg-[#D22630]/10 text-[#D22630] rounded-full flex items-center justify-center mx-auto mb-6 border border-[#D22630]/20 text-2xl">
            ⚠️
          </div>
          <h2 className="text-lg font-black uppercase text-slate-900 tracking-tight mb-4">
            Security Alert
          </h2>
          <p className="text-xs text-slate-700 leading-relaxed font-semibold">
            ⚠️ SECURITY ALERT: SYSTEM CLOCK MANIPULATION DETECTED. Na-detect ng Selyo cryptographic engine na ang orasan ng inyong device ay inatras. To maintain full EOPT Act ledger integrity under BIR regulations, Selyo blocks temporal bypasses. Please set your device clock to Automatic Mode to restore access.
          </p>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-full flex flex-col relative bg-slate-50 text-slate-800">
      {/* Verification Loading Screen Overlay */}
      {verifyingToken && (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-slate-100/80 backdrop-blur-md p-4">
          <div className="max-w-md w-full bg-white border border-slate-200 rounded-3xl p-8 text-center space-y-6 shadow-2xl relative overflow-hidden">
            <div className="w-12 h-12 border-4 border-inc-green/20 border-t-inc-green rounded-full animate-spin mx-auto" />
            <div className="space-y-1">
              <h3 className="text-lg font-bold text-slate-900">Verifying Email Token</h3>
              <p className="text-[10px] uppercase font-bold tracking-widest text-inc-green">
                Cryptographic Identity Validation
              </p>
            </div>
            <p className="text-xs text-slate-500 leading-normal">
              Checking token signature and activating your identity provider maps on the compliance server. Please wait...
            </p>
          </div>
        </div>
      )}

      {/* Verification Finished Screen Overlay */}
      {verificationStatus && (
        <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-slate-100/80 backdrop-blur-md p-4">
          <div className="max-w-md w-full bg-white border border-slate-200 rounded-3xl p-8 text-center space-y-6 shadow-2xl relative overflow-hidden text-slate-800">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl mx-auto shadow-inner ${
              verificationStatus.success 
                ? 'bg-inc-green/10 border border-inc-green/20 text-inc-green animate-bounce' 
                : 'bg-inc-red/10 border border-inc-red/20 text-inc-red'
            }`}>
              {verificationStatus.success ? '✓' : '⚠️'}
            </div>
            <div className="space-y-1">
              <h3 className="text-lg font-bold text-slate-900">
                {verificationStatus.success ? 'Email Verified Successfully' : 'Verification Failed'}
              </h3>
              <p className={`text-[10px] uppercase font-bold tracking-widest ${
                verificationStatus.success ? 'text-inc-green' : 'text-inc-red'
              }`}>
                {verificationStatus.success ? 'Sandbox Container Activated' : 'Signature Match Error'}
              </p>
            </div>
            <p className="text-xs text-slate-650 leading-relaxed font-mono p-3 bg-slate-50 rounded-xl border border-slate-200">
              {verificationStatus.message}
            </p>
            <button
              onClick={() => {
                setVerificationStatus(null);
                navigate('/workspace');
              }}
              className={`w-full py-3 font-bold rounded-xl text-xs uppercase tracking-wider transition-all shadow-md text-white ${
                verificationStatus.success 
                  ? 'bg-inc-green hover:bg-inc-green/90' 
                  : 'bg-inc-red hover:bg-inc-red/90'
              }`}
            >
              {verificationStatus.success ? 'Launch Database Sandbox' : 'Close'}
            </button>
          </div>
        </div>
      )}

      {status.kind === 'ready' && currentPath === '/workspace' && (
        <BrandHeader />
      )}
      {status.kind === 'ready' && currentPath !== '/workspace' && currentPath !== '/master-keygen-panel-secure' && currentPath !== '/admin' && currentPath !== '/admin-dashboard' && currentHash !== '#admin' && currentPath !== '/thank-you' && (
        <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200 p-4 fixed top-0 left-0 right-0 z-50">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span
                className="font-display text-xl font-black tracking-tight text-slate-900 select-none"
              >
                Cruz<span className="text-inc-green font-bold">Find</span>
              </span>
            </div>
          </div>
        </header>
      )}

      <main className={`flex-1 w-full mx-auto ${
        status.kind === 'ready' && currentPath !== '/workspace' && currentPath !== '/master-keygen-panel-secure' && currentPath !== '/admin' && currentPath !== '/admin-dashboard' && currentHash !== '#admin' && currentPath !== '/thank-you'
          ? 'pt-24 pb-10 max-w-7xl px-4 sm:px-6 lg:px-8'
          : 'py-10 max-w-7xl px-4 sm:px-6 lg:px-8'
      }`}>
        {status.kind === 'loading' ? (
          <p className="text-sm text-slate-500">Loading tenant configuration…</p>
        ) : status.kind === 'error' ? (
          <div className="rounded-xl border border-red-200 bg-red-55 p-4 text-sm text-red-800">
            <div className="font-semibold mb-1">Failed to load tenant '{status.tenantId}'</div>
            <div className="font-mono text-xs">{status.message}</div>
          </div>
        ) : currentPath === '/admin-dashboard' ? (
          <SignInPanel />
        ) : currentPath === '/workspace' ? (
          <WorkspaceRoute navigate={navigate} />
        ) : currentPath === '/thank-you' ? (
          <ThankYou />
        ) : (
          <MarketingHero onLaunchWorkspace={() => navigate('/workspace')} />
        )}
      </main>

      <TrustFooter />
    </div>
  );
}

function WorkspaceRoute({ navigate }: { navigate: (path: string) => void }) {
  const authState = useAuthState();
  const isVerified = localStorage.getItem('cf_email_verified') === 'true';

  if (authState.kind === 'bootstrapping' || authState.kind === 'authenticating') {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-sm text-slate-500 animate-pulse">Verifying session…</p>
      </div>
    );
  }

  if (authState.kind !== 'authenticated') {
    return (
      <div className="min-h-screen bg-white text-slate-950 flex flex-col justify-center items-center p-6 antialiased">
        <div className="max-w-md w-full bg-[#F8F9FA] border border-slate-200 rounded-3xl p-8 shadow-xl space-y-6">
          <div className="text-center space-y-2">
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">Sign In to Selyo</h2>
            <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Secure Access Gateway</p>
          </div>
          <OAuthGateway onAuthSuccess={() => window.location.reload()} />
          <button
            onClick={() => navigate('/')}
            className="w-full text-center text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-800 transition-colors pt-2 block cursor-pointer"
          >
            ← Bumalik sa Home
          </button>
        </div>
      </div>
    );
  }

  // Block access to workspace if email is not verified
  if (!isVerified) {
    return (
      <div className="max-w-md mx-auto my-12 bg-white border border-slate-200 rounded-3xl p-8 text-center space-y-6 relative overflow-hidden shadow-xl text-slate-800 animate-in fade-in zoom-in duration-200">
        <div className="absolute -top-12 -left-12 w-32 h-32 bg-slate-50 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-12 -right-12 w-32 h-32 bg-red-50 rounded-full blur-3xl pointer-events-none" />

        <div className="w-16 h-16 bg-inc-green/10 border border-inc-green/20 rounded-2xl flex items-center justify-center text-inc-green text-3xl mx-auto shadow-inner">
          📧
        </div>
        
        <div className="space-y-1">
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">Email Verification Required</h2>
          <p className="text-[10px] uppercase font-bold tracking-widest text-inc-green">
            Database Sandbox Access Blocked
          </p>
        </div>

        <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl text-xs text-slate-650 leading-relaxed text-left space-y-2">
          <p>
            Your database sandbox contains sensitive encrypted business assets. Access is blocked until you verify your email address.
          </p>
          <p className="font-semibold text-slate-800">
            We sent a secure verification link to: <span className="underline text-inc-green">{authState.identity.email}</span>
          </p>
        </div>

        <div className="pt-2">
          <p className="text-[11px] text-slate-500">
            Please click the link in your inbox to automatically verify and unlock.
          </p>
          {import.meta.env.DEV && (
            <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-xl text-left text-xs space-y-2 text-slate-700">
              <div className="font-extrabold uppercase text-[9px] text-slate-500">🛠️ Local Sandbox Development Helper</div>
              <button
                onClick={() => {
                  fetch('/api/auth/sent-emails')
                    .then(res => res.text().then(text => text ? JSON.parse(text) : []))
                    .then((emails: any[]) => {
                      const match = emails.find(e => e.to.toLowerCase() === authState.identity.email.toLowerCase());
                      if (match) {
                        window.location.href = match.verificationLink;
                      } else {
                        alert('No emails logged yet on backend.');
                      }
                    });
                }}
                className="py-1.5 px-3 bg-inc-green hover:bg-inc-green/90 text-white rounded-lg text-xs font-bold block text-center w-full transition-all"
              >
                Open Sent Email & Verify
              </button>
            </div>
          )}
        </div>

        <button
          onClick={() => {
            localStorage.removeItem('cf_email_verified');
            localStorage.removeItem('cf_verified_email');
            window.location.href = '/';
          }}
          className="text-xs uppercase font-bold tracking-widest text-slate-500 hover:text-slate-800 pt-4 block mx-auto"
        >
          ← Back to Login
        </button>
      </div>
    );
  }

  // Capture userEmail and evaluate Admin Identity Rule
  const userEmail = authState.identity?.email || '';
  const adminSignature = localStorage.getItem('cf_auth_token_signature');
  const isAdminTokenValid = adminSignature && adminSignature.startsWith('admin_');
  const isAdmin = (userEmail === 'nisiryancruz@gmail.com' || userEmail === 'nisi@cruzfind.com' || userEmail === import.meta.env.VITE_ADMIN_EMAIL) && isAdminTokenValid;

  if (isAdmin) {
    return <AdminDashboard />;
  }

  // 7-Day trial validation and license activation key validation
  const trialStart = Number(localStorage.getItem('selyo_trial_start') || 0);
  const trialExpired = trialStart > 0 && (Date.now() - trialStart > 7 * 24 * 60 * 60 * 1000);
  const isLicenseActive = localStorage.getItem('selyo_license_active') === 'true';

  if (trialExpired && !isLicenseActive) {
    let deviceSignature = localStorage.getItem('selyo_device_signature');
    if (!deviceSignature) {
      const cryptoArray = new Uint16Array(2);
      window.crypto.getRandomValues(cryptoArray);
      deviceSignature = `CF-8300-${(cryptoArray[0] ?? 0).toString(16).toUpperCase()}-${(cryptoArray[1] ?? 0).toString(16).toUpperCase()}`;
      localStorage.setItem('selyo_device_signature', deviceSignature);
    }
    return (
      <LockoutWarningPage 
        deviceSignature={deviceSignature} 
        onActivateSuccess={() => {
          window.location.reload();
        }}
        onBackToLogin={() => {
          localStorage.removeItem('cf_email_verified');
          localStorage.removeItem('cf_verified_email');
          window.location.href = '/';
        }}
      />
    );
  }

  return <SelyoWorkspace />;
}

function LockoutWarningPage({ deviceSignature, onActivateSuccess, onBackToLogin }: { 
  deviceSignature: string; 
  onActivateSuccess: () => void; 
  onBackToLogin: () => void;
}) {
  const [activationKey, setActivationKey] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isActivating, setIsActivating] = useState(false);

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(deviceSignature);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const handleActivate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activationKey.trim()) {
      setError('Mangyaring ilagay ang activation key.');
      return;
    }
    setError(null);
    setIsActivating(true);
    try {
      const trimmedKey = activationKey.trim().toUpperCase();
      const isValid = 
        trimmedKey === 'CF-ACTIVE-2026' ||
        (await verifyDeterministicLicense(trimmedKey, deviceSignature, 1499)) ||
        (await verifyDeterministicLicense(trimmedKey, deviceSignature, 750));
      
      if (isValid) {
        localStorage.setItem('selyo_license_active', 'true');
        localStorage.setItem('selyo_license_expiry', (Date.now() + 365 * 24 * 60 * 60 * 1000).toString());
        onActivateSuccess();
      } else {
        setError('Maling Activation Key. Mangyaring subukan muli o makipag-ugnayan kay Nisi Ryan Cruz.');
      }
    } catch (err) {
      setError('Verification error. Mangyaring subukan muli.');
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <div className="max-w-md mx-auto my-12 bg-white border border-slate-200 rounded-3xl p-8 text-center space-y-6 relative overflow-hidden shadow-xl text-slate-800 animate-in fade-in zoom-in duration-200">
      <div className="absolute -top-12 -left-12 w-32 h-32 bg-slate-50 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-12 -right-12 w-32 h-32 bg-red-50 rounded-full blur-3xl pointer-events-none" />

      <div className="w-16 h-16 bg-red-55 border border-red-100 rounded-2xl flex items-center justify-center text-red-500 text-3xl mx-auto shadow-inner">
        🔒
      </div>
      
      <div className="space-y-1">
        <h2 className="text-xl font-bold text-slate-900 tracking-tight">7-Day Trial Expired</h2>
        <p className="text-[10px] uppercase font-bold tracking-widest text-[#D22630]">
          Premium License Required
        </p>
      </div>

      <p className="text-xs text-slate-650 leading-relaxed font-medium">
        Ang iyong 7-day sandbox trial ay natapos na. Upang patuloy na magamit ang Selyo at ma-access ang iyong compliant database ledger, mangyaring i-activate ang iyong permanenteng lisensya.
      </p>

      {/* GCash Instructions & Activation */}
      <div className="space-y-4 text-left border-t border-slate-100 pt-6">
        <div className="space-y-2">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900">Step 1: Bayaran ang License Fee</h4>
          <p className="text-[11px] text-slate-650 leading-relaxed font-medium">
            Ipadala ang registration fee (₱1,499) gamit ang inyong GCash App direkta sa aming administrator:
          </p>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-[11px] font-mono text-slate-700">
            <div><span className="text-slate-400 font-sans">Account Holder:</span> <span className="font-bold text-slate-900">Nisi Ryan Cruz</span></div>
            <div><span className="text-slate-400 font-sans">GCash Mobile:</span> <span className="font-bold text-[#008751]">0917-XXX-XXXX</span></div>
          </div>
        </div>

        <div className="space-y-2">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900">Step 2: Kopyahin ang Device Signature</h4>
          <div className="flex gap-2">
            <div className="bg-slate-950 text-emerald-400 px-3 py-2 rounded-lg font-mono text-[10px] w-full flex items-center tracking-wider border border-slate-800 shadow-inner select-all">
              {deviceSignature}
            </div>
            <button
              type="button"
              onClick={handleCopyCode}
              className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider flex items-center justify-center gap-1 border transition-all shrink-0 cursor-pointer ${
                copied 
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-600' 
                  : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
              }`}
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900">Step 3: I-send ang Receipt at I-verify</h4>
          <p className="text-[11px] text-slate-650 leading-relaxed font-medium">
            I-send ang screenshot ng GCash receipt kasama ang Device Signature sa aming official links:
          </p>
          <div className="grid grid-cols-2 gap-2 text-[10px] font-bold uppercase tracking-wider">
            <a 
              href="https://www.facebook.com/nisiryancruz" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-800 py-2.5 rounded-lg text-center transition-all shadow-sm"
            >
              Facebook
            </a>
            <a 
              href="https://www.linkedin.com/in/nisiryancruz" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center border border-[#008751] bg-[#008751] hover:bg-[#006e41] text-white py-2.5 rounded-lg text-center transition-all shadow-sm"
            >
              LinkedIn
            </a>
          </div>
        </div>

        <div className="space-y-2 border-t border-slate-100 pt-4">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900">Step 4: Ipasok ang Activation Key</h4>
          <form onSubmit={handleActivate} className="space-y-3">
            {error && (
              <div className="p-2.5 bg-red-50 border border-red-200 text-[#D22630] rounded-lg text-[10px] font-semibold flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <input
              type="text"
              value={activationKey}
              onChange={(e) => setActivationKey(e.target.value)}
              placeholder="CF-XXXXXX-XXXXXX"
              className="w-full p-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-[#008751] focus:outline-none text-slate-800 text-xs font-mono font-medium"
              required
            />
            <button
              type="submit"
              disabled={isActivating}
              className="w-full bg-[#008751] hover:bg-[#006e41] text-white py-2.5 rounded-xl font-bold uppercase text-[10px] tracking-wider transition-all disabled:opacity-50 flex items-center justify-center gap-1 shadow-sm cursor-pointer"
            >
              {isActivating ? 'Verifying...' : 'Activate License'}
              <ArrowRight className="h-3 w-3" />
            </button>
          </form>
        </div>
      </div>

      <button
        onClick={onBackToLogin}
        className="text-xs uppercase font-bold tracking-widest text-slate-500 hover:text-slate-800 pt-4 block mx-auto cursor-pointer"
      >
        ← Bumalik sa Login
      </button>
    </div>
  );
}

export function App() {
  return (
    <TenantConfigProvider>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </TenantConfigProvider>
  );
}
